/**
 * Gadget Marvels product-card media: one behaviour layer for every product card.
 *
 * Any card media container marked [data-gm-card-media] that holds a <gm-card-media> marker gets:
 *   - a smooth crossfade to the second image on mouse hover (desktop pointers only), which stops
 *     applying once the shopper has moved to another slide themselves
 *   - in-card video: a video slide gets its source and plays muted/inline only while it is the
 *     active slide and the card is on screen; it pauses as soon as either stops being true
 *   - (custom cards with data-gm-href) click-to-open that ignores swipes and arrow clicks
 *
 * Swiping and arrows come from the theme's own <slideshow-component>. The marker element gives
 * a clean lifecycle: section reloads in the Theme Editor and cards inserted later (product
 * recommendations, search) connect and disconnect on their own, so listeners never pile up.
 *
 * Containers: snippets/card-gallery.liquid (standard Horizon product card) and
 * snippets/gm-card-media.liquid (homepage showcase and related-product cards).
 */

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const DRAG_THRESHOLD = 8; // px of pointer travel that turns a click into a swipe

/** @type {WeakMap<Element, GmCardMedia>} */
const ownerByRoot = new WeakMap();

const visibility = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      ownerByRoot.get(entry.target)?.setVisible(entry.isIntersecting);
    }
  },
  { rootMargin: '150px 0px', threshold: 0.01 }
);

class GmCardMedia extends HTMLElement {
  /** @type {HTMLElement | null} */
  #root = null;
  /** @type {AbortController | null} */
  #abort = null;
  #visible = false;
  #slideIndex = 0;
  /** @type {{x: number, y: number} | null} */
  #pointerStart = null;
  #dragged = false;

  connectedCallback() {
    const root = this.closest('[data-gm-card-media]');
    if (!(root instanceof HTMLElement) || ownerByRoot.has(root)) return;

    this.#root = root;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    ownerByRoot.set(root, this);

    root.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    root.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    root.addEventListener('slideshow:select', this.#onSelect, { signal });

    if (root.dataset.gmHref) {
      root.addEventListener('pointerdown', this.#onPointerDown, { signal });
      root.addEventListener('pointermove', this.#onPointerMove, { signal, passive: true });
      root.addEventListener('click', this.#onClick, { signal });
    }

    for (const video of root.querySelectorAll('video.gm-card-video')) {
      video.addEventListener('playing', () => video.parentElement?.classList.add('is-playing'), { signal });
    }

    visibility.observe(root);
  }

  disconnectedCallback() {
    const root = this.#root;
    this.#abort?.abort();
    this.#abort = null;
    if (!root) return;
    visibility.unobserve(root);
    this.#pauseAll();
    ownerByRoot.delete(root);
    this.#root = null;
  }

  /** @param {boolean} visible */
  setVisible(visible) {
    this.#visible = visible;
    if (!this.#root) return;

    if (visible) {
      // Warm the hover image just before it can be needed, on hover-capable screens only.
      if (finePointer.matches) {
        const hoverImage = this.#root.querySelector('.gm-card-hover img[loading="lazy"]');
        hoverImage?.setAttribute('loading', 'eager');
      }
      this.#syncVideo(this.#activeSlide());
    } else {
      this.#pauseAll();
    }
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    if (event.pointerType !== 'mouse' || !finePointer.matches || !this.#root) return;
    if (this.#slideIndex !== 0) return; // the shopper picked a slide; don't override it
    if (!this.#root.querySelector('.gm-card-hover')) return;
    this.#root.classList.add('is-gm-hover');
  };

  #onPointerLeave = () => {
    this.#root?.classList.remove('is-gm-hover');
  };

  /** @param {Event} event */
  #onSelect = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    // Ignore events bubbling up from a nested slideshow inside the card.
    if (detail.slide && detail.slide.closest('[data-gm-card-media]') !== this.#root) return;

    this.#slideIndex = typeof detail.index === 'number' ? detail.index : 0;
    if (this.#slideIndex !== 0) this.#root?.classList.remove('is-gm-hover');
    this.#syncVideo(detail.slide instanceof Element ? detail.slide : this.#activeSlide());
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#pointerStart = { x: event.clientX, y: event.clientY };
    this.#dragged = false;
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#pointerStart || this.#dragged) return;
    const dx = Math.abs(event.clientX - this.#pointerStart.x);
    const dy = Math.abs(event.clientY - this.#pointerStart.y);
    if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) this.#dragged = true;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const root = this.#root;
    if (!root || event.defaultPrevented) return;
    const target = /** @type {Element} */ (event.target);
    if (target.closest('button, a, slideshow-arrows, slideshow-controls')) return;
    if (this.#dragged) {
      this.#dragged = false;
      return;
    }
    const href = root.dataset.gmHref;
    if (!href) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) {
      window.open(href, '_blank');
    } else {
      window.location.href = href;
    }
  };

  /** @returns {Element | null} */
  #activeSlide() {
    const slideshow = this.#root?.querySelector('slideshow-component');
    if (slideshow && 'slides' in slideshow && 'current' in slideshow) {
      const slides = /** @type {any} */ (slideshow).slides;
      const current = /** @type {any} */ (slideshow).current;
      if (slides && typeof current === 'number') return slides[current] ?? null;
    }
    return this.#root?.querySelector('slideshow-slide') ?? null;
  }

  /** @param {Element | null} slide */
  #syncVideo(slide) {
    const root = this.#root;
    if (!root) return;

    for (const video of root.querySelectorAll('video.gm-card-video')) {
      if (!slide || !slide.contains(video)) this.#pause(video);
    }

    const video = slide?.querySelector('video.gm-card-video');
    if (!(video instanceof HTMLVideoElement) || !this.#visible) return;

    if (!video.getAttribute('src') && video.dataset.gmSrc) {
      video.src = video.dataset.gmSrc;
      video.preload = 'auto';
    }
    video.muted = true; // never any sound
    const attempt = video.play();
    if (attempt) attempt.catch(() => {});
  }

  #pauseAll() {
    for (const video of this.#root?.querySelectorAll('video.gm-card-video') ?? []) this.#pause(video);
  }

  /** @param {Element} video */
  #pause(video) {
    if (video instanceof HTMLVideoElement && !video.paused) video.pause();
  }
}

if (!customElements.get('gm-card-media')) {
  customElements.define('gm-card-media', GmCardMedia);
}
